import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { getErrorMessage } from '../../../utils/extractErrorMessage';

interface ViewPromptDialogProps {
  open: boolean;
  onClose: () => void;
  promptName: string;
  /** ai-prompts branch the body was fetched from; undefined when the picker flag is off. */
  branch?: string;
  /** Branch head commit SHA the body was fetched at — displayed short. */
  commitSha: string | null;
  body: string | null;
  isLoading: boolean;
  error?: unknown;
}

/** Prompt bodies are internal prompt-repo text, rendered verbatim as text — never as markup. */
export const ViewPromptDialog = ({
  open,
  onClose,
  promptName,
  branch,
  commitSha,
  body,
  isLoading,
  error,
}: ViewPromptDialogProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const serverErrorMessage = getErrorMessage(error);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
        <DescriptionOutlinedIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {promptName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatMessage(
              { id: 'adminProjects.generateAccuracy.viewPromptDialog.subtitle' },
              { branch: branch ?? '—', sha: commitSha ? commitSha.slice(0, 7) : '—' }
            )}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: theme.spacing(6) }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">
            {serverErrorMessage ||
              formatMessage({ id: 'adminProjects.generateAccuracy.viewPromptDialog.loadError' })}
          </Alert>
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: theme.spacing(3),
              borderRadius: 1,
              backgroundColor: theme.palette.grey[100],
              fontFamily: 'monospace',
              fontSize: '14px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {body}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
